import { useState } from 'react';
import {
  Alert,
  AttachmentGallery,
  Button,
  CopyButton,
  EmptyState,
  ReadonlyTextArea,
  TextArea,
  linkTextClasses,
} from '@evogard/ui';
import type {
  ClaimActivity,
  ClaimComment,
  ClaimFile,
  ClaimMessage,
} from '@evogard/contract';
import {
  CLAIM_DEPARTMENTS,
  analysisHoldLabel,
  catalogValueText,
  formatInstant,
  type CatalogField,
  type ClaimDepartment,
} from '@evogard/domain';
import { useSessionContext } from '../../../lib/session.js';
import { deskOf } from './desk.js';
import { DEPARTMENT_LABEL, phaseLabel } from './labels.js';
import { claimFileUrl, useDeskMutations } from './useDesk.js';

/* ------------------------------------------------------------------------- *
 * Atividade
 * ------------------------------------------------------------------------- */

type ActivityFilter = 'tudo' | 'comentarios' | 'alteracoes';

const FILTERS: { value: ActivityFilter; label: string }[] = [
  { value: 'tudo', label: 'Tudo' },
  { value: 'comentarios', label: 'Comentários' },
  { value: 'alteracoes', label: 'Alterações' },
];

/** O departamento gravado, se é um que a casa conhece; sem ele, a Análise (os eventos antigos). */
const departmentOf = (department: string | null | undefined): ClaimDepartment =>
  department && department in DEPARTMENT_LABEL
    ? (department as ClaimDepartment)
    : 'analise';

/**
 * O nome de uma fase, pelo catálogo do departamento dela — a Entrada da
 * Análise e a do Reparo têm o mesmo código (07/10/2026).
 */
const phaseName = (phase: string | null, department?: string | null) =>
  phase ? phaseLabel(departmentOf(department), phase) : '';

/** O nome de um campo de anexo, pelo `kind` que o catálogo do departamento dá a ele. */
function claimFileFieldLabel(department: string | null, phase: string, kind: string) {
  if (kind === 'anexo_aviso') return 'Arquivos do aviso';
  /* O print vem da tentativa de contato, e não de um campo (15/10/2026). */
  if (kind === 'print_contato') return 'Evidência de contato';
  /* Os arquivos que a aba Despesas envia ao card (26/10/2026). */
  if (kind === 'despesa_documento') return 'Documento da despesa';
  if (kind === 'despesa_nota_fiscal') return 'Nota fiscal da despesa';
  const fields = deskOf(departmentOf(department))?.phaseOf(phase)?.fields;
  const field = fields?.find((f) => {
    const b = f.binding as { subject: string; kind?: string };
    return b.subject === 'document' && b.kind === kind;
  });
  return field?.label ?? kind;
}

/**
 * Como a mesa nomeia as coisas do card — o sinistro ou o acionamento de
 * Periféricos (15/10/2026). A Atividade e os Anexos são os mesmos para os
 * dois; muda o nome do card, o das fases, o dos campos e as frases dos
 * eventos que só um deles grava.
 */
export interface DeskVocabulary {
  /** O card, sem artigo: "sinistro", "acionamento". Os dois são masculinos. */
  noun: string;
  /** Quem vê o comentário — a dica da caixa de comentar. */
  commentHint: string;
  /** De onde vêm os arquivos — o vazio da aba Anexos. */
  filesHint: string;
  departmentName: (department: string | null) => string | null;
  phaseName: (phase: string | null, department?: string | null) => string;
  /** O campo do formulário de uma alteração, para mostrar o valor como a pessoa o lê. */
  formField: (
    department: string | null,
    phase: string,
    key: string,
  ) => CatalogField | undefined;
  /** O nome do campo de onde veio um arquivo. */
  fileFieldLabel: (department: string | null, phase: string, kind: string) => string;
  assigneeLabel: (department: string | null) => string;
  /** A frase dos eventos que só este card grava; `undefined` cai na frase comum. */
  sentence?: (item: ActivityChange) => string | undefined;
}

export type ActivityChange = Extract<ClaimActivity, { kind: 'alteracao' }>;

export const CLAIM_VOCABULARY: DeskVocabulary = {
  noun: 'sinistro',
  commentHint: 'Escreva um comentário — todos os departamentos veem',
  filesHint: 'Os arquivos entram pelos campos de anexo de cada fase, e pelo aviso.',
  departmentName: (department) =>
    department ? DEPARTMENT_LABEL[departmentOf(department)] : null,
  phaseName,
  formField: (department, phase, key) => {
    const desk = deskOf(departmentOf(department));
    if (!desk?.phaseOf(phase)) return undefined;
    return desk.visibleFields(phase).find((f) => f.key === key);
  },
  fileFieldLabel: claimFileFieldLabel,
  assigneeLabel: (department) =>
    deskOf(departmentOf(department))?.assigneeLabel.toLowerCase() ?? 'responsável',
};

/**
 * As mutações de comentário que a linha do tempo usa — as do sinistro
 * (`useDeskMutations`) ou as do acionamento (`usePeripheralCommentMutations`).
 */
interface Mutation<V> {
  mutate(variables: V, options?: { onSuccess?: () => void }): void;
  isPending: boolean;
  isError: boolean;
  error: Error | null;
}
export interface CommentMutations {
  comment: Mutation<string>;
  editComment: Mutation<{ commentId: number; body: string }>;
  removeComment: Mutation<number>;
}

/**
 * O texto de um valor gravado na trilha. Com o campo do catálogo da fase, o
 * valor sai como a pessoa o lê — a opção pelo rótulo, o dinheiro em reais, a
 * data em dd/mm/aaaa (`analysisValueText`); sem ele (a correção do aviso, que
 * não é campo de fase), sai como está, com Sim e Não traduzidos.
 */
function shown(value: unknown, field?: CatalogField): string {
  if (value === null || value === undefined || value === '') return 'vazio';
  if (field && typeof value === 'string') return catalogValueText(field, value);
  if (value === 'sim') return 'Sim';
  if (value === 'nao') return 'Não';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * A frase de uma alteração. O tipo é o que a trilha gravou; o tipo que esta
 * tela não conhece aparece cru, em vez de sumir.
 */
function sentenceOf(item: ActivityChange, v: DeskVocabulary): string {
  const own = v.sentence?.(item);
  if (own !== undefined) return own;
  const phaseName = v.phaseName;
  const where = phaseName(item.phase, item.department);
  switch (item.type) {
    case 'criado':
      return `abriu o ${v.noun}`;
    case 'movido': {
      /* O envio a outro departamento diz o departamento (07/10/2026). */
      const from = phaseName(item.fromPhase, item.department);
      return item.toDepartment
        ? `enviou de ${from} para ${v.departmentName(item.toDepartment)}`
        : `moveu de ${from} para ${phaseName(item.toPhase, item.department)}`;
    }
    case 'movimento_desfeito':
      return `desfez o movimento e voltou para ${phaseName(item.toPhase, item.department)}`;
    case 'formulario_salvo':
      return item.changes.length === 1
        ? `preencheu 1 campo em ${where}`
        : `preencheu ${item.changes.length} campos em ${where}`;
    case 'corrigido':
      return item.changes.length === 1
        ? 'corrigiu 1 campo do aviso'
        : `corrigiu ${item.changes.length} campos do aviso`;
    case 'anexo_adicionado':
      return `anexou ${item.fileName ?? 'um arquivo'}${where ? ` em ${where}` : ''}`;
    case 'anexo_arquivado':
      return `tirou ${item.fileName ?? 'um arquivo'}${where ? ` de ${where}` : ''}`;
    /* Os registros que não mudam a etapa (06/10/2026). */
    case 'contato_registrado':
      return `registrou uma tentativa de contato${item.detail ? ` (${item.detail})` : ''}${where ? ` em ${where}` : ''}`;
    case 'parado':
      return `parou o ${v.noun}${where ? ` em ${where}` : ''} por ${analysisHoldLabel(item.holdType ?? '').toLowerCase()}${item.detail ? `: ${item.detail}` : ''}`;
    case 'retomado':
      return `retomou o ${v.noun}${where ? ` em ${where}` : ''}${item.detail ? `: ${item.detail}` : ''}`;
    /* O checklist da Indenização (09/10/2026) — `detail` é "documento — arquivo". */
    case 'documento_validado':
      return `conferiu ${item.detail ?? 'um documento'}`;
    case 'validacao_desfeita':
      return `desfez a conferência de ${item.detail ?? 'um documento'}`;
    /* Os débitos da Indenização (§5.9) — `detail` é o débito, e o valor ou a situação. */
    case 'debito_incluido':
      return `incluiu o débito ${item.detail ?? ''}`.trim();
    case 'debito_corrigido':
      return `corrigiu o débito ${item.detail ?? ''}`.trim();
    case 'debito_situacao':
      return `mudou a situação do débito ${item.detail ?? ''}`.trim();
    case 'situacao_mudou':
      return `${where ? `${where}: ` : ''}a situação passou a ${(item.detail ?? '').toLowerCase()}`;
    case 'responsavel_definido': {
      const role = v.assigneeLabel(item.department);
      return item.detail ? `definiu ${item.detail} como ${role}` : `tirou o ${role}`;
    }
    /* O rodízio de responsáveis (20/10/2026) — o ator é o sistema. */
    case 'responsavel_distribuido':
      return `passou o ${v.noun} para ${item.detail ?? 'o próximo da vez'} pelo rodízio${where ? `, ao entrar em ${where}` : ''}`;
    case 'responsavel_devolvido':
      return `devolveu o ${v.noun} a ${item.detail ?? 'quem o tinha'}, que já cuidava dele${where ? ` em ${where}` : ''}`;
    /* A fase do supervisor (29/10/2026): quem cuidava deixa de cuidar, e o
       rodízio dela não tinha quem recebesse. `detail` é quem deixou. */
    case 'responsavel_liberado':
      return `tirou ${item.detail ?? 'o responsável'} do ${v.noun}${where ? ` ao entrar em ${where}` : ''}, que fica sem responsável`;
    /* As etiquetas (07/10/2026) — `detail` é a lista de nomes do ato. */
    case 'etiqueta_posta':
      return `pôs ${item.detail?.includes(',') ? 'as etiquetas' : 'a etiqueta'} ${item.detail ?? ''}`.trim();
    case 'etiqueta_tirada':
      return `tirou ${item.detail?.includes(',') ? 'as etiquetas' : 'a etiqueta'} ${item.detail ?? ''}`.trim();
    /* O vencimento do card no departamento (07/10/2026) — `detail` é o instante. */
    case 'vencimento_definido':
      return item.detail
        ? `definiu o vencimento para ${formatInstant(item.detail)}`
        : 'tirou o vencimento';
    /* Dos Encerrados (13/10/2026) — o porquê é o comentário logo ao lado. */
    case 'reaberto':
      return `reabriu o ${v.noun}, de ${phaseName(item.fromPhase, item.department)} para ${phaseName(item.toPhase, item.department)}`;
    /* O concluído do Reparo que volta pela garantia (22/10/2026) — `detail` é o motivo. */
    case 'garantia_acionada':
      return `acionou a garantia${item.detail ? `: ${item.detail}` : ''}`;
    /* A CAF (16/10/2026) — `detail` é a situação do resultado. */
    case 'caf_link_gerado':
      return 'gerou o link da CAF';
    case 'caf_resultado':
      return `recebeu o resultado da CAF${item.detail ? ` — ${item.detail}` : ''}`;
    case 'arquivado':
      return `arquivou o ${v.noun}`;
    case 'reativado':
      return `reativou o ${v.noun}`;
    default:
      return item.type;
  }
}

/**
 * A Atividade (item 11): uma linha do tempo só, do mais novo para o mais
 * velho — comentários em balão, mudanças de fase e campos preenchidos. Os
 * campos salvos de uma vez são UMA linha, que abre a lista do antes e do
 * depois. A caixa de comentar fica no alto; o comentário é do sinistro, e
 * todos os departamentos o veem.
 *
 * É a MESMA atividade na mesa da Análise e na ficha do aviso (`ClaimPanel`):
 * o comentário feito numa aparece na outra, porque os dois leem
 * `GET /claims/:id/activity`.
 */
export function ActivityTab({
  claimId,
  ...rest
}: {
  claimId: number;
  items: ClaimActivity[] | undefined;
  loading: boolean;
  error: Error | null;
  canComment: boolean;
  /** Frases prontas, que entram no fim do rascunho — a ficha do aviso as oferece. */
  quickPhrases?: readonly string[];
}) {
  /* Comentar não é da mesa de nenhum departamento: vale em todos. */
  const mutations = useDeskMutations(null, claimId);
  return (
    <ActivityTimeline {...rest} mutations={mutations} vocabulary={CLAIM_VOCABULARY} />
  );
}

/**
 * A linha do tempo, sem saber de quem é: o card chega pelas mutações e pelo
 * vocabulário. É a Atividade do sinistro (`ActivityTab`) e a do acionamento
 * de Periféricos.
 */
export function ActivityTimeline({
  items,
  loading,
  error,
  canComment,
  quickPhrases = [],
  mutations,
  vocabulary,
}: {
  items: ClaimActivity[] | undefined;
  loading: boolean;
  error: Error | null;
  canComment: boolean;
  quickPhrases?: readonly string[];
  mutations: CommentMutations;
  vocabulary: DeskVocabulary;
}) {
  const [filter, setFilter] = useState<ActivityFilter>('tudo');
  const [body, setBody] = useState('');
  const { data: context } = useSessionContext();
  const me = context?.user.id ?? null;

  const visible = (items ?? []).filter((a) =>
    filter === 'tudo'
      ? true
      : filter === 'comentarios'
        ? a.kind === 'comentario'
        : a.kind === 'alteracao',
  );

  const send = () => {
    const text = body.trim();
    if (!text) return;
    mutations.comment.mutate(text, { onSuccess: () => setBody('') });
  };

  return (
    <div className="flex flex-col gap-4">
      {canComment && (
        <div className="flex flex-col gap-2">
          <TextArea
            label="Comentar"

            placeholder={vocabulary.commentHint}
            rows={2}
            maxLength={4000}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          {quickPhrases.length > 0 && (
            <div
              role="group"
              aria-label="Frases prontas"
              className="flex flex-wrap gap-2"
            >
              {quickPhrases.map((phrase) => (
                <Button
                  key={phrase}
                  size="sm"
                  variant="discreto"
                  onClick={() =>
                    setBody((current) =>
                      current.trim() ? `${current.trim()} ${phrase}` : phrase,
                    )
                  }
                >
                  {phrase}
                </Button>
              ))}
            </div>
          )}
          {mutations.comment.isError && (
            <Alert tone="erro">{mutations.comment.error?.message}</Alert>
          )}
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={send}
              disabled={body.trim() === ''}
              loading={mutations.comment.isPending}
            >
              Comentar
            </Button>
          </div>
        </div>
      )}

      <div role="group" aria-label="Filtrar a atividade" className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <Button
            key={f.value}
            size="sm"
            variant={filter === f.value ? 'secundario' : 'discreto'}
            aria-pressed={filter === f.value}
            onClick={() => setFilter(f.value)}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {loading && <p className="text-sm text-texto-suave">Carregando a atividade…</p>}
      {error && <Alert tone="erro">{error.message}</Alert>}
      {items && visible.length === 0 && (
        <EmptyState icon="historico" title="Nada por aqui ainda">
          {filter === 'comentarios'
            ? `Ninguém comentou neste ${vocabulary.noun}.`
            : 'Nenhuma alteração registrada.'}
        </EmptyState>
      )}

      <ol className="flex flex-col gap-3">
        {visible.map((item) =>
          item.kind === 'comentario' ? (
            <CommentItem
              key={item.key}
              mutations={mutations}
              vocabulary={vocabulary}
              comment={item.comment}
              mine={item.comment.authorId === me && canComment}
            />
          ) : (
            <ChangeItem key={item.key} item={item} vocabulary={vocabulary} />
          ),
        )}
      </ol>
    </div>
  );
}

/**
 * Um comentário, na MESMA forma da alteração: quem, quando e onde numa linha,
 * e o texto embaixo. Balão é linguagem de conversa (Demandas); aqui a linha
 * do tempo é uma só, e o comentário é mais uma coisa que aconteceu no
 * sinistro — decisão do dono em 05/10/2026.
 */
function CommentItem({
  mutations,
  vocabulary,
  comment,
  mine,
}: {
  mutations: CommentMutations;
  vocabulary: DeskVocabulary;
  comment: ClaimComment;
  mine: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(comment.body);
  const where = [
    vocabulary.departmentName(comment.department),
    vocabulary.phaseName(comment.phase, comment.department),
  ]
    .filter(Boolean)
    .join(' · ');
  const author = comment.authorName ?? 'Sem nome';

  if (comment.archivedAt) {
    return (
      <li className="rounded-borda border border-borda-suave px-3 py-2 text-sm text-texto-tenue">
        Comentário removido por {author}
        <span> · {formatInstant(comment.archivedAt)}</span>
      </li>
    );
  }

  return (
    <li className="flex flex-col gap-1 rounded-borda border border-borda-suave px-3 py-2 text-sm">
      <p>
        <span className="font-semibold">{author}</span> comentou
        {/* O campo do formulário que pediu o comentário é o porquê dele (20/10/2026). */}
        {comment.fieldLabel && (
          <>
            {' em '}
            <span className="font-semibold">{comment.fieldLabel}</span>
          </>
        )}
        <span className="text-texto-tenue">
          {' '}
          · {formatInstant(comment.createdAt)}
          {where && ` · ${where}`}
          {comment.editedAt && ' · editado'}
        </span>
      </p>
      {editing ? (
        <div className="flex flex-col gap-2">
          <TextArea
            label="Editar o comentário"
            rows={2}
            maxLength={4000}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="discreto"
              onClick={() => {
                setBody(comment.body);
                setEditing(false);
              }}
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              disabled={body.trim() === ''}
              loading={mutations.editComment.isPending}
              onClick={() =>
                mutations.editComment.mutate(
                  { commentId: comment.id, body: body.trim() },
                  { onSuccess: () => setEditing(false) },
                )
              }
            >
              Salvar
            </Button>
          </div>
        </div>
      ) : (
        <p className="whitespace-pre-line text-texto">{comment.body}</p>
      )}
      {mine && !editing && (
        <div className="-ml-2 flex gap-1">
          <Button size="sm" variant="discreto" onClick={() => setEditing(true)}>
            Editar
          </Button>
          <Button
            size="sm"
            variant="discreto"
            loading={mutations.removeComment.isPending}
            onClick={() => mutations.removeComment.mutate(comment.id)}
          >
            Remover
          </Button>
        </div>
      )}
    </li>
  );
}

/** Uma alteração: quem, o quê e quando; os campos salvos abrem a lista. */
function ChangeItem({
  item,
  vocabulary,
}: {
  item: ActivityChange;
  vocabulary: DeskVocabulary;
}) {
  const [open, setOpen] = useState(false);
  return (
    <li className="flex flex-col gap-1 rounded-borda border border-borda-suave px-3 py-2 text-sm">
      <p>
        <span className="font-semibold">{item.actorName ?? 'O sistema'}</span>{' '}
        {sentenceOf(item, vocabulary)}
        <span className="text-texto-tenue"> · {formatInstant(item.at)}</span>
      </p>
      {item.changes.length > 0 && (
        <>
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className={linkTextClasses('self-start text-xs')}
          >
            {open ? 'Esconder os campos' : 'Ver os campos'}
          </button>
          {open && (
            <ul className="flex flex-col gap-0.5 text-xs text-texto-suave">
              {item.changes.map((c) => {
                const field =
                  item.type === 'formulario_salvo' && item.phase
                    ? vocabulary.formField(item.department, item.phase, c.field)
                    : undefined;
                return (
                  <li key={c.field}>
                    <span className="font-semibold text-texto">{c.label}</span>:{' '}
                    {shown(c.before, field)} → {shown(c.after, field)}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </li>
  );
}

/* ------------------------------------------------------------------------- *
 * Anexos
 * ------------------------------------------------------------------------- */

/**
 * Os Anexos (item 11): os arquivos de todas as fases por onde o aviso passou,
 * agrupados por departamento e fase, com o campo de origem. Arquivo novo entra
 * pelo campo dele no formulário, não por aqui — esta aba só lê.
 */
export function FilesTab({
  claimId,
  ...rest
}: {
  claimId: number;
  files: ClaimFile[] | undefined;
  loading: boolean;
  error: Error | null;
}) {
  return (
    <FilesList
      {...rest}
      vocabulary={CLAIM_VOCABULARY}
      resolveUrl={(documentId) => claimFileUrl(claimId, documentId)}
    />
  );
}

/** Os Anexos, sem saber de quem é o card — o sinistro ou o acionamento de Periféricos. */
export function FilesList({
  files,
  loading,
  error,
  vocabulary,
  resolveUrl,
}: {
  files: ClaimFile[] | undefined;
  loading: boolean;
  error: Error | null;
  vocabulary: DeskVocabulary;
  resolveUrl: (documentId: number) => Promise<string>;
}) {
  if (loading) return <p className="text-sm text-texto-suave">Carregando os anexos…</p>;
  if (error) return <Alert tone="erro">{error.message}</Alert>;
  if (!files || files.length === 0) {
    return (
      <EmptyState icon="clipe" title="Nenhum arquivo ainda">
        {vocabulary.filesHint}
      </EmptyState>
    );
  }

  /* Grupo = departamento + fase, na ordem em que o primeiro arquivo entrou. */
  const groups = new Map<string, ClaimFile[]>();
  for (const f of files) {
    const key = `${f.department ?? ''}|${f.phase}`;
    groups.set(key, [...(groups.get(key) ?? []), f]);
  }

  return (
    <div className="flex flex-col gap-5">
      {[...groups.entries()].map(([key, list]) => {
        const first = list[0]!;
        const department = vocabulary.departmentName(first.department);
        const byField = new Map<string, ClaimFile[]>();
        for (const f of list) {
          const label = vocabulary.fileFieldLabel(f.department, f.phase, f.kind);
          byField.set(label, [...(byField.get(label) ?? []), f]);
        }
        return (
          <section key={key} className="flex flex-col gap-3">
            <h4 className="text-xs font-semibold tracking-wide text-texto-tenue uppercase">
              {[department, vocabulary.phaseName(first.phase, first.department)]
                .filter(Boolean)
                .join(' · ')}
            </h4>
            {[...byField.entries()].map(([label, docs]) => (
              <AttachmentGallery
                key={label}
                label={label}
                items={docs.map((d) => ({
                  key: String(d.id),
                  nome: d.fileName,
                  typeMime: d.mimeType,
                  sizeBytes: d.sizeBytes,
                  detail: [d.createdByName, formatInstant(d.createdAt)]
                    .filter(Boolean)
                    .join(' · '),
                  resolveUrl: () => resolveUrl(d.id),
                }))}
              />
            ))}
          </section>
        );
      })}
    </div>
  );
}

/**
 * As mensagens de envio do sinistro (15/10/2026): os scripts que a mesa copia
 * e manda ao associado, consultados em qualquer fase. Agrupadas por
 * departamento, com o de agora primeiro — cada departamento tem as suas, e as
 * dos outros ficam abaixo, para consulta.
 *
 * Ninguém digita: a mensagem nasce do link dela, e a que ainda não nasceu
 * aparece vazia, dizendo de onde virá. O departamento sem mensagem prevista diz isso, em vez de
 * sumir.
 */
export function MessagesTab({
  department,
  messages,
  loading,
  error,
}: {
  /** O departamento da mesa aberta — o grupo dele vem primeiro. */
  department: ClaimDepartment | null;
  messages: ClaimMessage[] | undefined;
  loading: boolean;
  error: Error | null;
}) {
  if (error) return <Alert tone="erro">{error.message}</Alert>;
  if (loading || !messages) {
    return <p className="text-sm text-texto-suave">Carregando as mensagens…</p>;
  }
  const order = [
    ...(department ? [department] : []),
    ...CLAIM_DEPARTMENTS.filter((d) => d !== department),
  ];

  return (
    <div className="flex flex-col gap-6">
      {order.map((d) => {
        const own = messages.filter((m) => m.department === d);
        return (
          <section key={d} aria-label={`Mensagens · ${DEPARTMENT_LABEL[d]}`}>
            <h3 className="mb-2 text-xs font-semibold tracking-wide text-texto-tenue uppercase">
              {DEPARTMENT_LABEL[d]}
            </h3>
            {own.length === 0 ? (
              <p className="text-sm text-texto-suave">
                Nenhuma mensagem neste departamento ainda.
              </p>
            ) : (
              <div className="flex flex-col gap-4">
                {own.map((m) => (
                  <div key={m.kind} className="flex flex-col gap-2">
                    <ReadonlyTextArea
                      label={m.label}
                      value={m.body}
                      placeholder={`Ainda não gerada: ela nasce quando ${m.source} for preenchido.`}
                      help={m.body ? `Refeita sempre que ${m.source} muda.` : undefined}
                    />
                    {m.body && (
                      <div className="self-start">
                        <CopyButton text={m.body} label={`Copiar ${m.label}`} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
